import { useCallback, useMemo, useRef, type FC } from "react";
import type { EditorRef } from "react-email-editor";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import CampaignFlowActions from "src/layouts/Engagements/Campaigns/shared/CampaignFlowActions";
import EmailStatusPopup from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/EmailStatusPopup";
import { CampaignStepCardChromeContext } from "src/layouts/Engagements/Campaigns/shared/CampaignStepCard";
import SaveDraftModal from "src/layouts/Engagements/Campaigns/shared/SaveDraftModal";
import ChatScheduleStep from "./ChatScheduleStep";
import CreateCampaignStep from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/steps/CreateCampaignStep";
import { useChatDraft } from "./UseChatDraft";
import { useCampaignDelivery } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/hooks/UseCampaignDelivery";
import { useRecommendedProducts } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/hooks/UseRecommendedProducts";
import { saveEditorDesign } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/EmailEditorUtils";
import ChooseTemplateStep from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/Template/steps/ChooseTemplateStep";
import SenderRecipientsStep from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/steps/SenderRecipientsStep";
import TemplatePreviewModal from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/Template/components/TemplatePreviewModal";
import { useEmailTemplates } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/Template/hooks/UseEmailTemplates";
import {
  EMAIL_TEMPLATE_WIZARD_STEPS,
  TEMPLATE_EDITOR_KEY,
} from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/Template/TemplateEditorConstants";
import type { ChatTemplateStepSnapshot, useChatTemplateCampaignWizard } from "./UseChatTemplateCampaignWizard";
import "src/layouts/Engagements/Campaigns/shared/CampaignFlow.scss";
import "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/AiCampaignWizard.scss";
import "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/Template/TemplateEmailEditor.scss";
import "./ChatCampaignWizard.scss";

const WIZARD_KEY = "campaignFlow.aiWizard";
const noop = () => undefined;

export type { ChatTemplateStepSnapshot as FrozenTemplateSnapshot } from "./UseChatTemplateCampaignWizard";

interface ChatTemplateCampaignWizardProps {
  wizard: ReturnType<typeof useChatTemplateCampaignWizard>;
  frozenSnapshot?: ChatTemplateStepSnapshot;
  onDraftSaved?: () => void;
}

const ChatTemplateCampaignWizard: FC<ChatTemplateCampaignWizardProps> = ({
  wizard,
  frozenSnapshot,
  onDraftSaved,
}) => {
  const { t, i18n } = useTranslation();
  const editorRef = useRef<EditorRef>(null);
  const frozen = Boolean(frozenSnapshot);
  const templatesState = useEmailTemplates();

  const stepIndex = frozenSnapshot?.stepIndex ?? wizard.stepIndex;
  const stepKey = frozenSnapshot?.stepKey ?? wizard.stepKey;
  const template = frozenSnapshot?.template ?? wizard.template;
  const details = frozenSnapshot?.details ?? { campaignName: wizard.campaignName, subjectLine: wizard.subjectLine };

  const {
    preview,
    renderingTemplateId,
    senderDetails,
    sendTo,
    audienceOptions,
    isListsLoading,
    canProceed,
    setSubjectLine,
    setReplyTo,
    setSendTo,
    openPreview,
    closePreview,
    applyTemplate,
    goNext,
    goBack,
    saveTemplateDesign,
    draft,
  } = wizard;

  const isEditorStep = stepKey === "editor";
  const isWideStep = stepKey !== "senderRecipients";
  const recommendedProducts = useRecommendedProducts({
    audienceNames: senderDetails.recipients,
    excludeAudienceNames: senderDetails.exclusions,
    campaignName: details.campaignName,
    enabled: isEditorStep && !frozen,
  });

  const {
    isSending,
    isScheduleOpen,
    deliveryStatus,
    sendNow,
    schedule,
    openSchedule,
    closeSchedule,
    clearDeliveryStatus,
  } = useCampaignDelivery({
    editorRef,
    template,
    campaignName: details.campaignName,
    subjectLine: details.subjectLine,
    senderDetails,
    replyTo: senderDetails.replyTo,
    draftId: draft.draftId,
    onDelivered: draft.close,
  });

  const stepCountLabel = useMemo(
    () =>
      t("wizardStepper.stepOf", {
        step: formatNumber(stepIndex + 1, {}, i18n.language),
        total: formatNumber(EMAIL_TEMPLATE_WIZARD_STEPS.length, {}, i18n.language),
      }),
    [i18n.language, stepIndex, t]
  );

  const isBusy = Boolean(renderingTemplateId) || isSending;

  const { save: saveWizardDraft } = draft;
  const saveDraft = useCallback(async () => {
    const design = isEditorStep ? await saveEditorDesign(editorRef.current?.editor) : null;
    if (design) {
      saveTemplateDesign(design);
    }
    return saveWizardDraft(design);
  }, [isEditorStep, saveTemplateDesign, saveWizardDraft]);
  const { isDraftOpen, openDraft, closeDraft, handleSaveDraft } = useChatDraft(saveDraft, onDraftSaved);

  const handleBack = useCallback(async () => {
    if (isEditorStep) {
      const design = await saveEditorDesign(editorRef.current?.editor);
      if (design) {
        saveTemplateDesign(design);
      }
    }
    goBack();
  }, [goBack, isEditorStep, saveTemplateDesign]);

  const renderStep = () => {
    switch (stepKey) {
      case "senderRecipients":
        return (
          <SenderRecipientsStep
            subjectLine={details.subjectLine}
            onSubjectLineChange={frozen ? noop : setSubjectLine}
            senderDetails={senderDetails}
            sendTo={sendTo}
            audienceOptions={audienceOptions}
            isListsLoading={isListsLoading}
            onReplyToChange={setReplyTo}
            onSendToChange={setSendTo}
          />
        );
      case "chooseTemplate":
        return (
          <ChooseTemplateStep
            templatesState={templatesState}
            brandName={senderDetails.fromName}
            renderingTemplateId={renderingTemplateId}
            onPreview={openPreview}
            onUse={applyTemplate}
          />
        );
      default:
        return (
          <CreateCampaignStep
            title={t(`${TEMPLATE_EDITOR_KEY}.editor.title`)}
            subtitle={t(`${TEMPLATE_EDITOR_KEY}.editor.subtitle`)}
            editorRef={editorRef}
            template={template}
            recommendedProducts={recommendedProducts}
          />
        );
    }
  };

  const footer = frozen || !isScheduleOpen ? (
      <CampaignFlowActions
        onBack={handleBack}
        backDisabled={isBusy}
        wide={isWideStep}
        note={isEditorStep ? undefined : t(`${WIZARD_KEY}.chatNote`)}
      >
        <button
          type="button"
          className="button button--link campaign-flow__action campaign-flow__action--link"
          onClick={openDraft}
          disabled={isBusy}
        >
          {t("campaignFlow.common.saveDraft")}
        </button>
        {isEditorStep ? (
          <>
            <button
              type="button"
              className="button button--primary campaign-flow__action"
              onClick={openSchedule}
              disabled={isSending}
            >
              {t(`${WIZARD_KEY}.createCampaign.scheduleCampaign`)}
            </button>
            <button
              type="button"
              className="button button--primary campaign-flow__action"
              onClick={sendNow}
              disabled={isSending}
            >
              {isSending ? t(`${WIZARD_KEY}.createCampaign.sending`) : t(`${WIZARD_KEY}.createCampaign.sendCampaign`)}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="button button--primary campaign-flow__action"
            onClick={goNext}
            disabled={!canProceed || isBusy}
          >
            {t(`${WIZARD_KEY}.next`)}
          </button>
        )}
      </CampaignFlowActions>
  ) : undefined;

  const chrome = { stepLabel: stepCountLabel, footer };

  const frozenClass = frozen ? " chat-campaign-wizard--frozen" : "";

  return (
    <div className={`campaign-flow chat-campaign-wizard${frozenClass}`}>
      <CampaignStepCardChromeContext.Provider value={chrome}>{renderStep()}</CampaignStepCardChromeContext.Provider>

      {!frozen && (
        <SaveDraftModal
          open={isDraftOpen}
          isSaving={draft.isSaving}
          onKeepEditing={closeDraft}
          onSaveAndExit={handleSaveDraft}
        />
      )}

      {!frozen && preview && (
        <TemplatePreviewModal
          templateId={preview.templateId}
          html={preview.html}
          isApplying={renderingTemplateId === preview.templateId}
          onClose={closePreview}
          onUse={applyTemplate}
        />
      )}

      {!frozen && isScheduleOpen && (
        <ChatScheduleStep isSubmitting={isSending} onCancel={closeSchedule} onSchedule={schedule} />
      )}

      {!frozen && deliveryStatus && (
        <EmailStatusPopup
          type={deliveryStatus.type}
          date={deliveryStatus.date}
          time={deliveryStatus.time}
          onClose={clearDeliveryStatus}
        />
      )}
    </div>
  );
};

export default ChatTemplateCampaignWizard;
